import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps react-bootstrap dropdowns stable inside the legacy application shell.
 * The shell's click handling can otherwise make an uncontrolled dropdown open
 * and immediately close from the same click.
 */
export const useControlledDropdown = () => {
  const [show, setShow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setShow(false), []);
  const toggle = useCallback(() => setShow((isOpen) => !isOpen), []);

  useEffect(() => {
    if (!show) return undefined;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [close, show]);

  return { close, containerRef, show, toggle };
};
